/**
 * Admin utilities for checking user permissions
 */

export interface UserData {
  username?: string;
  email?: string;
  display_name?: string;
  id?: string;
  auth_method?: string;
}

/** Primary operator account (tools, paper trading). Compared case-insensitively. */
export const MMEC_PRIMARY_ADMIN_EMAIL = 'g.althawr@gmail.com';

/**
 * Check if the current user is an admin
 * @returns boolean indicating if user is admin
 */
export const isAdmin = (): boolean => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return false;

    const user: UserData = JSON.parse(userData);
    const userEmail = (user.email || user.username || '').trim().toLowerCase();

    return userEmail === MMEC_PRIMARY_ADMIN_EMAIL;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get current user data from localStorage
 * @returns UserData object or null if not found
 */
export const getCurrentUser = (): UserData | null => {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return null;
    
    return JSON.parse(userData) as UserData;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Check if user has specific admin permission
 * @param permission - The permission to check
 * @returns boolean indicating if user has permission
 */
export const hasAdminPermission = (permission: string): boolean => {
  if (!isAdmin()) return false;
  
  // For now, all admins have all permissions
  // This can be extended later for role-based permissions
  return true;
};

/**
 * Admin email addresses that have access (lowercase)
 */
export const ADMIN_EMAILS = [MMEC_PRIMARY_ADMIN_EMAIL] as const;

/**
 * Check if an email is in the admin list
 * @param email - Email to check
 * @returns boolean indicating if email is admin
 */
export const isAdminEmail = (email: string): boolean => {
  const n = (email || '').trim().toLowerCase();
  return ADMIN_EMAILS.some((e) => e === n);
};

/** @deprecated use isMmecPrimaryAdminUser / isAdminEmail */
export const NEW_RTL_BETA_EMAIL = MMEC_PRIMARY_ADMIN_EMAIL;

/**
 * True when the logged-in user is the primary MMEC admin (email match or explicit `isAdmin` from API).
 */
export function isMmecPrimaryAdminUser(
  user: { email?: string; username?: string; isAdmin?: boolean } | null | undefined
): boolean {
  if (!user) return false;
  if (user.isAdmin === true) return true;
  const email = (user.email || user.username || '').trim().toLowerCase();
  return email === MMEC_PRIMARY_ADMIN_EMAIL;
}

/**
 * Pro nav **Tools** rail (Trading Calls, Call Tracker / paper, Calls Widget, Call Generator):
 * **only** {@link MMEC_PRIMARY_ADMIN_EMAIL} — ignores `user.isAdmin` so other accounts stay hidden.
 */
export function isMmecToolsRailUser(
  user: { email?: string; username?: string; isAdmin?: boolean } | null | undefined,
): boolean {
  if (!user) return false;
  const email = (user.email || user.username || '').trim().toLowerCase();
  return email === MMEC_PRIMARY_ADMIN_EMAIL;
}

/**
 * Check if the current user can see the New RTL data source option.
 * New RTL is hidden for everyone except this beta user until it is ready.
 * @param user - Optional user from useAuth(); if not passed, uses getCurrentUser() from storage.
 */
export const canSeeNewRtl = (user?: UserData | { email?: string; username?: string } | null): boolean => {
  const u = user ?? getCurrentUser();
  if (!u) return false;
  const email = (u.email || u.username || '').trim().toLowerCase();
  return email === NEW_RTL_BETA_EMAIL.toLowerCase();
}; 